// FEATURE-86 — self-locating launcher.
//
// The launcher derives the *assistant origin* from its own `<script>` element
// so a host site on a DIFFERENT origin still loads the panel iframe (and the
// API) from the assistant host instead of resolving `/panel/` against the
// host page's origin (→ 404 → blank iframe).
//
// `document.currentScript` is only non-null during the synchronous top-level
// run of a classic (deferred) script — which is exactly when this module is
// first evaluated by the UMD bundle. We capture that reference EAGERLY here.
// For async / module / dynamically-injected includes, `currentScript` is null,
// so we fall back to querying for a `<script src*="widget.js">`.

const SCRIPT_SRC_NEEDLE = 'widget.js';

// Eagerly captured at module-eval time (when currentScript is still valid).
// `null` in non-browser/test contexts and for async/injected scripts.
const initialCurrentScript: HTMLScriptElement | null =
  typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

let cachedScript: HTMLScriptElement | null | undefined;

function findLauncherScript(): HTMLScriptElement | null {
  // Prefer the script that was executing when this module first loaded.
  if (initialCurrentScript !== null) return initialCurrentScript;
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLScriptElement>(`script[src*="${SCRIPT_SRC_NEEDLE}"]`);
}

/**
 * The launcher's own `<script>` element, or `null` when it can't be located
 * (no `widget.js` script in the DOM, or a non-browser context).
 */
export function getLauncherScript(): HTMLScriptElement | null {
  if (cachedScript === undefined) cachedScript = findLauncherScript();
  return cachedScript;
}

/**
 * The origin the launcher bundle was served from, e.g. `http://192.168.50.186`.
 * `null` when the script element can't be found or its `src` is unparseable
 * (callers then fall back to the build-time / same-origin behavior).
 */
export function getAssistantOrigin(): string | null {
  const script = getLauncherScript();
  if (script === null) return null;
  try {
    // `script.src` is already an absolute URL per the DOM spec.
    return new URL(script.src).origin;
  } catch {
    return null;
  }
}

/** Test-only — clear the memoized script lookup so the DOM is re-queried. */
export function __resetScriptOriginForTests(): void {
  cachedScript = undefined;
}
